import {useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {NewsletterSubscriptionService_SubscribeToNewsletterLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.grpc.lazy';
import {Gender} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useContactRoute, usePrivacyPolicyRoute} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  COLORS,
  Disclaimer,
  FONT_SIZES,
  FONT_WEIGHTS,
  LoadingButton,
  RadioButton,
  RadioGroup,
  Text,
  TextInput,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {useOptionalSession, useOptionalUser} from '@aboutyou/user';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {useForm} from 'react-hook-form';
import {useSelectedTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';

interface FormData {
  email: string;
  gender: 'female' | 'male';
}

export const NewsletterSubscriptionForm: React.FC<{
  cta: string;
  onSuccess: VoidFunction;
  onError: VoidFunction;
  className?: string;
}> = ({cta, onSuccess, onError, className}) => {
  const optionalUser = useOptionalUser();
  const {targetGroup: selectedTargetGroup} = useSelectedTargetGroup();
  const {t} = useTranslation();
  const session = useOptionalSession();
  const trackFeature = useTrackFeature();
  const privacyPolicyRoute = usePrivacyPolicyRoute();
  const contactRoute = useContactRoute();

  const subscribeToNewsletterCallback = useGrpcRequestCallback(
    NewsletterSubscriptionService_SubscribeToNewsletterLazy,
  );

  const {
    register,
    handleSubmit,
    getValues,
    formState: {errors, isSubmitting},
  } = useForm<FormData>({
    defaultValues: {
      email: optionalUser.data?.userData?.email,
      gender: selectedTargetGroup === TargetGroup.MALE ? 'male' : 'female',
    },
    mode: 'onSubmit',
  });

  const submitHandler = async ({email, gender}: FormData) => {
    try {
      await subscribeToNewsletterCallback({
        email,
        gender: gender === 'male' ? Gender.MALE : Gender.FEMALE,
        session,
      });
      trackFeature({
        eventType: 'submit',
        name: 'newsletter',
      });
      onSuccess();
    } catch (error) {
      onError();
    }
  };

  return (
    <Form
      autoComplete="on"
      onSubmit={handleSubmit(submitHandler)}
      method="POST"
      className={className}
      aria-label={t('Newsletter subscription form')}
    >
      <div>
        <EmailInput
          type="email"
          autoComplete="email"
          value={getValues('email')}
          errorMessage={errors.email?.message}
          label={t('Your email address')}
          {...register('email', {
            required: t('Please enter your email address'),
            pattern: {
              value: emailRegex,
              message: t('This is not a valid email address'),
            },
          })}
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
        />

        <GenderContainer>
          <GenderLabel>{t('Receive exclusive offers')}</GenderLabel>

          <GenderRadioGroup>
            <GenderRadioButton
              label={t('For women')}
              {...register('gender')}
              value="female"
              id="newsletter-gender-female"
            />
            <GenderRadioButton
              label={t('For men')}
              {...register('gender')}
              value="male"
              id="newsletter-gender-male"
            />
          </GenderRadioGroup>
        </GenderContainer>
      </div>

      <StyledDisclaimer
        text={t(
          'I would like to receive the ABOUT YOU newsletter on trends and offers (see {privacyPolicy}) by email. The consent can be withdrawn at any time via a newsletter email or customer service ({helpCenter}).',
          {
            privacyPolicy: `<a rel="noopener noreferrer" target="_blank" href="${privacyPolicyRoute}#section3.3.2.">${t(
              'Privacy Policy',
            )}</a>`,
            helpCenter: `<a rel="noopener noreferrer" target="_blank" href="${contactRoute}">${t(
              'Contact us | ABOUT YOU Help Center',
            )}</a>`,
          },
        )}
      />

      <Cta disableShadow isLoading={isSubmitting}>
        {cta}
      </Cta>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${tabletAndUpLinaria(`

    justify-content: space-between;
    flex: 1;
  `)}
`;

const EmailInput = styled(TextInput)`
  padding-bottom: 16px;
`;

const GenderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;

  ${tabletAndUpLinaria(`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0;
  `)}
`;

const GenderLabel = styled(Text)`
  display: block;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.4;

  ${tabletAndUpLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;

const GenderRadioButton = styled(RadioButton)`
  padding: 12px 0;
`;

const GenderRadioGroup = styled(RadioGroup)`
  ${GenderRadioButton} + ${GenderRadioButton} {
    margin-left: 12px;
  }
`;

const StyledDisclaimer = styled(Disclaimer)`
  font-size: ${FONT_SIZES.px10};
  text-align: left;
`;

const Cta = styled(LoadingButton)`
  width: 100%;
  margin: 16px 0 24px 0;

  ${tabletAndUpLinaria(`
    margin-bottom: 0;
  `)}
`;
